import { cn } from "@moe/oss/utils/utils";
import { Persona } from "@moe/priv/model/persona";
import { useQuery } from "@tanstack/react-query";
import { PersonaList } from "@web/components/persona/PersonaList";
import { useAppContext } from "@web/route-services/root/AppContext";
import { toast } from "sonner";

interface Props {
  selectedID?: number;
  onSelect?: (persona: Persona) => void;
  className?: string;
}

/**
 * A modal that allows users to select a persona for use in a chat.
 * Also allows them to create, edit, delete personas.
 */
export function PersonaSelectorModal({ selectedID, onSelect, className }: Props) {
  const { access, closeModal } = useAppContext();

  const { data: personasData, isPending } = useQuery({
    queryKey: ["selfPersonas"],
    queryFn: async () => {
      return access.persona.getSelf();
    }
  });

  const selected = personasData?.find((p) => p.id === selectedID);

  const personas: Persona[] = personasData || [];
  if (selected) personas.unshift(selected);

  const handleClick = async (persona: Persona) => {
    if (onSelect) onSelect(persona);
    closeModal();
    toast.success(`Selected ${persona.name} as the persona for this chat.`);
  };

  return (
    <div
      className={cn(
        "flex max-h-[75vh] min-h-24 w-full flex-col space-y-9 overflow-y-auto rounded-2xl p-6 font-medium md:w-[26rem]",
        className
      )}
    >
      <div className="ml-3 flex flex-col">
        <h1 className="text-tx-primary text-xl font-semibold">Personas</h1>
        <h2 className="text-tx-secondary text font-medium">Select a persona to use for this chat.</h2>
      </div>
      <PersonaList personas={personas} isPending={isPending} onClick={handleClick} />
    </div>
  );
}
